import { Table } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";

import ExportManager from "./ExportManager";

import { ExportFileType } from "@/app/constants/constants";
import { Button } from "@/components/ui/button";
import {
  ExtendedColumnDef,
  _TFloatingMenuCustomComponentProps,
  _TTableFloatingMenuCustomComponentProps,
} from "@/interfaces/globals";
import { cn } from "@/utils/core/generalUtils";

type DataTableActionsFloatingMenuProps<TData, TValue> = {
  children?: React.ComponentType<_TTableFloatingMenuCustomComponentProps>;
  className?: string;
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
  table?: Table<TData>;
  isExport?: boolean;
  allowedExportOptions?: ExportFileType[];
  multiFloating?: {
    multiIds: string[];
    handleUnSelectAll: () => void;
  };
};

export const DataTableActionsFloatingMenu = <D, V>({
  columns,
  data,
  table,
  isExport,
  children,
  multiFloating,
  allowedExportOptions,
}: DataTableActionsFloatingMenuProps<D, V>): React.JSX.Element => {
  const rowSelection = table?.getState().rowSelection;
  const ids = useMemo(() => {
    if (multiFloating) {
      return multiFloating.multiIds;
    }

    if (rowSelection) {
      return Object.entries(rowSelection)
        .filter(([, isSelected]) => isSelected)
        .map(([rowId]) => rowId);
    }

    return [];
  }, [multiFloating, rowSelection]);

  const [state, setState] = useState(false);

  useEffect(() => {
    if (ids.length > 0) {
      setState(true);
    } else {
      setState(false);
    }
  }, [ids.length]);

  const Component = children || Fragment;

  const handleClose = (): void => {
    setState(false);
    if (multiFloating) {
      multiFloating?.handleUnSelectAll();

      return;
    }
    table?.setRowSelection({});
  };

  return (
    <div className='fixed bottom-8 z-50 left-1/2 -translate-x-1/2'>
      <div
        data-state={state}
        className={cn(
          "border py-1.5 px-4 rounded-md flex items-center divide-x-2 bg-background transition-all ease-in-out duration-300 data-[state=true]:animate-in data-[state=false]:animate-out data-[state=true]:slide-in-from-bottom-16 data-[state=false]:slide-out-to-bottom-16 data-[state=false]:opacity-0 data-[state=true]:opacity-100 data-[state=false]:size-0 overflow-hidden data-[state=false]:p-0 data-[state=false]:border-0",
        )}
      >
        <div className='flex items-center gap-4 mr-4'>
          <Button size='sm' variant='ghost' onClick={handleClose}>
            <X size={16} />
          </Button>
          <span className='text-sm font-medium text-nowrap'>
            {ids.length} selected
          </span>
        </div>

        <div className='pl-4 flex items-center'>
          {isExport && (
            <ExportManager<D, V>
              columns={columns}
              data={data}
              table={table}
              allowedOptions={allowedExportOptions}
            />
          )}
          <Component
            {...(children ? { ids, handleClose, close: handleClose } : {})}
          />
        </div>
      </div>
    </div>
  );
};
